/**
 * MSAL Context Provider for organization-specific authentication
 */

import { PublicClientApplication, Configuration } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import React, { createContext, useContext, useState } from 'react';

import { log } from '../utils/errors/logger';

interface OrganizationConfig {
  id: string;
  name: string;
  slug: string;
  tenant_id: string;
  client_id: string;
  base_url: string;
}

interface MsalOrgContextType {
  organization: OrganizationConfig | null;
  initializeForOrganization: (slug: string) => Promise<void>;
  isInitialized: boolean;
  error: string | null;
  loading: boolean;
}

const MsalOrgContext = createContext<MsalOrgContextType | null>(null);

interface MsalOrgProviderProps {
  children: React.ReactNode;
}

export const MsalOrgProvider: React.FC<MsalOrgProviderProps> = ({ children }) => {
  const [organization, setOrganization] = useState<OrganizationConfig | null>(null);
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const initializeForOrganization = async (slug: string) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch organization configuration from API
      // Use production-aware API URL
      const baseUrl = import.meta.env.PROD 
        ? `${window.location.protocol}//${window.location.host}`
        : 'http://localhost:8080';
      const orgResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || `${baseUrl  }/api`}/organizations/by-slug/${slug}`);
      
      if (!orgResponse.ok) {
        throw new Error('Organization not found');
      }

      const orgData = await orgResponse.json();
      
      // Find Entra provider
      const entraProvider = orgData.auth_providers?.find((p: any) => p.provider_type === 'entra' && p.is_primary);
      
      if (!entraProvider) {
        throw new Error('Organization does not use Microsoft Entra authentication');
      }

      const config = entraProvider.config || {};
      const { tenant_id, client_id, base_url } = config;

      if (!tenant_id || !client_id) {
        throw new Error('Incomplete Entra configuration - missing tenant_id or client_id');
      }

      const orgConfig: OrganizationConfig = {
        id: orgData.id,
        name: orgData.name,
        slug: orgData.slug,
        tenant_id,
        client_id,
        base_url: base_url || window.location.origin
      };

      // Create MSAL configuration
      const msalConfig: Configuration = {
        auth: {
          clientId: client_id,
          authority: `https://login.microsoftonline.com/${tenant_id}`,
          redirectUri: `${window.location.origin}/login/${slug}/callback`,
          postLogoutRedirectUri: `${window.location.origin}/login/${slug}`,
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: false,
        },
        system: {
          loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
              if (!containsPii) {
                log.debug(`MSAL [${level}]: ${message}`);
              }
            },
          },
        },
      };

      // Initialize MSAL instance
      const instance = new PublicClientApplication(msalConfig);
      await instance.initialize();

      setOrganization(orgConfig);
      setMsalInstance(instance);
      setIsInitialized(true);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize MSAL');
      log.error('MSAL initialization failed', { error: err instanceof Error ? err.message : String(err), slug });
    } finally {
      setLoading(false);
    }
  };

  const contextValue: MsalOrgContextType = {
    organization,
    initializeForOrganization,
    isInitialized,
    error,
    loading,
  };

  // If MSAL is not initialized or no instance, render without MsalProvider
  if (!isInitialized || !msalInstance) {
    return (
      <MsalOrgContext.Provider value={contextValue}>
        {children}
      </MsalOrgContext.Provider>
    );
  }

  // Render with MsalProvider when initialized
  return (
    <MsalOrgContext.Provider value={contextValue}>
      <MsalProvider instance={msalInstance}>
        {children}
      </MsalProvider>
    </MsalOrgContext.Provider>
  );
};

export const useMsalOrg = (): MsalOrgContextType => {
  const context = useContext(MsalOrgContext);
  if (!context) {
    throw new Error('useMsalOrg must be used within a MsalOrgProvider');
  }
  return context;
};